import React, { useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useColorModeValue,
  Box,
} from '@chakra-ui/react';
import { useTable, useSortBy } from 'react-table';
import { prettifyDate } from '../../../../functions/dateUtils';

const ReferredUsersTable = ({ users, showApplicationStatus }) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'Name',
        accessor: 'fullName',
        width: '200px',
      },
      {
        Header: 'Company',
        accessor: 'companyName',
        width: '200px',
      },
      {
        Header: 'Registration Date',
        accessor: 'registrationDate',
        Cell: ({ value }) => prettifyDate(value),
        width: '150px',
      },
      {
        Header: 'Days Since Registration',
        accessor: 'daysSinceRegistration',
        Cell: ({ value }) => (
          <Badge colorScheme={value > 30 ? 'red' : value > 14 ? 'yellow' : 'green'}>
            {value} days
          </Badge>
        ),
        width: '150px',
      },
    ];

    if (showApplicationStatus) {
      baseColumns.push(
        {
          Header: 'First Application',
          accessor: 'firstApplicationDate',
          Cell: ({ value }) => value ? prettifyDate(value) : '-',
          width: '150px',
        },
        {
          Header: 'Total Applications',
          accessor: 'totalApplications',
          Cell: ({ value }) => (
            <Badge colorScheme="blue">
              {value}
            </Badge>
          ),
          width: '120px',
        },
        {
          Header: 'Days to First App',
          accessor: 'daysToFirstApplication',
          Cell: ({ value }) => value ? (
            <Badge colorScheme={value <= 3 ? 'green' : value <= 7 ? 'yellow' : 'red'}>
              {value} days
            </Badge>
          ) : '-',
          width: '130px',
        }
      );
    }

    return baseColumns;
  }, [showApplicationStatus]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: users,
      initialState: {
        sortBy: [
          {
            id: 'registrationDate',
            desc: true,
          },
        ],
      },
    },
    useSortBy
  );

  if (!users || users.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">No users in this category</Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto">
      <Table {...getTableProps()} variant="simple" size="sm">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, columnIndex) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={columnIndex}
                  color="gray.400"
                  fontSize="xs"
                  fontWeight="700"
                  textTransform="uppercase"
                  borderColor={borderColor}
                  cursor={column.canSort ? 'pointer' : 'default'}
                  _hover={column.canSort ? { color: 'blue.400' } : {}}
                >
                  {column.render('Header')}
                  {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={index}>
                {row.cells.map((cell, cellIndex) => (
                  <Td
                    {...cell.getCellProps()}
                    key={cellIndex}
                    fontSize="sm"
                    color={textColor}
                    borderColor={borderColor}
                  >
                    {cell.render('Cell')}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

export default ReferredUsersTable;
